import * as R from 'ramda';
import { v4 as uuid } from 'uuid';

import { Button, Icon, IconButton, TextField } from '@atom/mui';
import {
  AddIcon,
  ArrowForwardIcon,
  CloseIcon,
  LinkIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { SubField, ValueDictionary } from '@atom/types/work';
import {
  initialNestedField,
  mapDropdownField,
  MIN_SINGLE_SELECT_OPTIONS,
  preventOptionLineBreak,
  removeOptionSubFields,
  toSingleLineOption,
} from '@atom/utilities/workOrderFieldUtilities';

import classes from './customFieldEnumEdit.module.css';

const styles = {
  button: {
    padding: '0',
    marginLeft: '5px',
  },
  addOptionButton: {
    marginTop: '0.5rem',
  },
};

interface Props {
  valueDictionary: ValueDictionary;
  enumeration: string[];
  subFields: SubField[];
  setEnumeration: (enumeration: string[]) => void;
  setValueDictionary: (valueDictionary: ValueDictionary) => void;
  maxOptions?: number;
  hideLinkOptions?: boolean;
  setSubFields: (subFields: SubField[]) => void;
  setActiveKey: (activeKey: string) => void;
}

const CustomFieldEnumRootEdit = ({
  valueDictionary,
  setValueDictionary,
  enumeration,
  setEnumeration,
  maxOptions,
  hideLinkOptions = false,
  subFields,
  setSubFields,
  setActiveKey,
}: Props) => {
  const addOption = () => {
    const id = uuid();

    setEnumeration([...enumeration, id]);
    setValueDictionary({
      ...valueDictionary,
      [id]: '',
    });
  };

  const updateOption = (event, key: string) => {
    setValueDictionary({
      ...valueDictionary,
      [key]: toSingleLineOption(event.target.value),
    });
  };

  const removeOption = (key: string) => {
    const updatedEnumeration = R.without([key], enumeration);

    setEnumeration(updatedEnumeration);

    setValueDictionary(R.omit([key], valueDictionary));

    setSubFields(
      removeOptionSubFields(subFields, key, updatedEnumeration.length),
    );
  };

  const addSubField = (key: string) => {
    const mappedField = mapDropdownField(initialNestedField);
    setValueDictionary({ ...valueDictionary, ...mappedField.valueDictionary });

    const updatedSubFields = [
      ...subFields,
      {
        matchValue: key,
        nestedField: R.omit(['valueDictionary'], mappedField),
      },
    ];

    setActiveKey(key);
    setSubFields(updatedSubFields);
  };

  const isAtMaxOptions = !!maxOptions && enumeration.length >= maxOptions;

  return (
    <>
      {!!maxOptions && (
        <div className={classes.maxOptionsHelper}>
          {`${maxOptions} max options for list`}
        </div>
      )}
      {enumeration.map((key: string, index: number) => {
        const subField = R.find(R.propEq(key, 'matchValue'))(subFields);

        return (
          <div key={key} className={classes.optionInputContainer}>
            <div className={classes.optionNumber}>{`${index + 1}.`}</div>
            <TextField
              id={key}
              value={valueDictionary[key]}
              key={key}
              onChange={event => updateOption(event, key)}
              onKeyDown={preventOptionLineBreak}
              multiline
              data-cy={`customFieldOptionInput-${index}`}
              style={{ width: '100%' }}
            />
            <IconButton
              style={styles.button}
              onClick={(): void => removeOption(key)}
              data-cy={`customFieldOptionRemoveButton-${index}`}
              disabled={enumeration.length <= MIN_SINGLE_SELECT_OPTIONS}
            >
              <Icon icon={CloseIcon} />
            </IconButton>
            {!hideLinkOptions &&
              !subField &&
              enumeration.length > MIN_SINGLE_SELECT_OPTIONS && (
                <IconButton
                  style={styles.button}
                  onClick={(): void => addSubField(key)}
                  tooltip="Link Question"
                  data-cy={`customFieldOptionLinkButton-${index}`}
                >
                  <Icon icon={LinkIcon} />
                </IconButton>
              )}
            {subField && (
              <IconButton
                style={styles.button}
                onClick={() => setActiveKey(key)}
              >
                <Icon icon={ArrowForwardIcon} />
              </IconButton>
            )}
          </div>
        );
      })}
      <div>
        <Button
          color="primary"
          startIcon={
            <Icon
              color={isAtMaxOptions ? colors.neutral.gray : colors.brand.blue}
              icon={AddIcon}
            />
          }
          onClick={addOption}
          style={styles.addOptionButton}
          disabled={isAtMaxOptions}
          data-cy="customFieldAddOptionButton"
        >
          Add Option
        </Button>
      </div>
    </>
  );
};

export default CustomFieldEnumRootEdit;
